
import { useI18n } from 'vue-i18n';
import {
  defineComponent, ref, reactive, watch, onMounted, onBeforeUnmount, computed, nextTick,
} from 'vue';
import { useRoute } from 'vue-router';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import useClipboard from 'vue-clipboard3';
import {
  Button,
  DropdownMenu,
  DropdownItem,
  Cell,
  CellGroup,
  CountDown,
  Toast,
  Loading,
  Overlay,
  Icon,
  Popover,
  ActionSheet,
  Skeleton,
  Calendar,
  DatetimePicker,
  NumberKeyboard,
  Dialog,
  Form,
  Field,
  Checkbox,
} from 'vant';
import { resverText, isMobile, isCanSerialize } from '@/utils/index';
import { evaluateEmbedGuard, resolveParentOrigin, tryGetCachedEmbedPolicy, setCachedEmbedPolicy, shouldSkipSourceReport, markSourceReportDone, getPayTokenFromLocation } from '@/utils/embedGuard';
import { baseURL } from '@/api/index';
import Realtime from '@/utils/realtime';
import { trackEvent } from '@/utils/getGtag';
import { ensureCheckoutWebComponentsReady, ensureNuveiCheckoutReady } from '@/utils/loadScript';
import { getWetechDevicePayload } from '@/utils/wetech';
import * as Cashier from '@/api/cashier';
import '@vant/touch-emulator';
import {
  resolvePayfacSuccessRedirectDelayMs,
  shouldShowPayfacIframeLoadingMask,
} from './payfacTransition.js';
import {
  InterfaceUI,
  Languages,
  FormatDateZone,
  FormData,
  OptionObject,
  CashPayInOrderStatus,
  ChannelObject,
  ChannelCurrencyRatesObject,
  SupportedBanksList,
  SupportedCountries,
  isUnlimitPayfacChannel,
  isPayablChannel,
  shouldTopOpenPayablRedirect,
  DynamicsFieldItem,
  PayinFormData,
} from './interface';
// import CheckoutWebComponents from "@checkout.com/checkout-web-components";
import KP_SELECT from '@/components/KP_SELECT.vue';
import KP_INPUT from '@/components/KP_INPUT.vue';
import KP_PICKER from '@/components/KP_PICKER.vue';

const env = process.env.VUE_APP_BUILD_ENV;
const key = `${env}-checkout-card`;

/** Vant 组件 name 常为 string | undefined，计算属性键需收窄为 string */
const vanName = (comp: { name?: string } | Record<string, any>) =>
  String((comp as { name?: string }).name || '')

export default defineComponent({
  name: 'payin',
  components: {
    VueQrcode,
    [vanName(Form)]: Form,
    [vanName(Checkbox)]: Checkbox,
    [vanName(Field)]: Field,
    [vanName(Button)]: Button,
    [vanName(DropdownMenu)]: DropdownMenu,
    [vanName(DropdownItem)]: DropdownItem,
    [vanName(Cell)]: Cell,
    [vanName(CellGroup)]: CellGroup,
    [vanName(CountDown)]: CountDown,
    [vanName(Toast)]: Toast,
    [vanName(Loading)]: Loading,
    [vanName(Overlay)]: Overlay,
    [vanName(Icon)]: Icon,
    [vanName(Popover)]: Popover,
    [vanName(ActionSheet)]: ActionSheet,
    [vanName(Skeleton)]: Skeleton,
    [vanName(Calendar)]: Calendar,
    [vanName(DatetimePicker)]: DatetimePicker,
    [vanName(NumberKeyboard)]: NumberKeyboard,
    [vanName(Dialog)]: Dialog,
    KP_SELECT,
    KP_INPUT,
    KP_PICKER,
  },
  setup() {
    const { BASE_URL } = process.env;
    const route = useRoute();
    const { t, locale } = useI18n();
    const { lang, from } = route.query;
    // 优先从 location 读 token：兼容手机复制链接把 & 变成 &amp; 后 route.query.k 丢失的情况
    const payToken = ref(
      String(getPayTokenFromLocation() || route.query.k || route.query.merOrderNo || route.query.id || '').trim(),
    );
    const isSunpayBuild = process.env.VUE_APP_BUILD_ENV.includes('sunpay');
    const isOpacity = ref(true);
    /** AP/GP 跳公共页期间保持遮罩，避免闪出 Sun Pay 选类型页 */
    const suppressPageReveal = ref(false);
    /**
     * 无动态字段自动 CreatePayIn：展示上保持 loading/透明，不露出「选支付类型」
     * 仅影响展示，不改变创单分支与接口调用
     */
    const silentAutoPreOrder = ref(false);
    /** 自动创单后即使 orderStatus 仍为 0，也不再露出选类型（防闪屏/二次点 Pay） */
    const autoPreOrderHidePaytype = ref(false);
    const showSupplierSheet = ref(false);
    const showBankSheet = ref(false);
    const result = ref(false);
    const showPopover = ref(false);
    const currentLang = ref();
    const loaded = ref(false);
    const showCalendar = ref(false);
    const remeberCheck = ref(false);
    const isLoadingToast = ref(isSunpayBuild);
    const beginSilentAutoPreOrder = () => {
      silentAutoPreOrder.value = true;
      autoPreOrderHidePaytype.value = true;
      isLoadingToast.value = true;
      isOpacity.value = true;
    };
    const endSilentAutoPreOrder = () => {
      if (!silentAutoPreOrder.value) return;
      silentAutoPreOrder.value = false;
    };
    /** 目标 UI 就绪后再揭开：结束静默 + 关 loading（opacity 由调用方按分支决定） */
    const revealAfterSilentTargetReady = (opts?: { opacity?: boolean }) => {
      endSilentAutoPreOrder();
      isLoadingToast.value = false;
      if (opts?.opacity !== false) {
        isOpacity.value = false;
      }
    };
    const showPayfacCardFormEarly = ref(!!(window as any).__VGPAY_SKIP_PAYTYPE__);
    const embedGuardReady = ref(false);
    const showPayfacTransition = ref(false);
    const showPayfacIframeLoadingMask = ref(false);
    const payfacTransitionStatus = ref('PROCESSING');
    const payfacTransitionStartedAt = ref(0);
    const payfacSuccessMinDisplayMs = 800;

    /**
     * 判断支付回跳地址是否安全可用。
     *
     * 业务流程：
     * 1. 解析 URL，拒绝非法格式
     * 2. 允许 http/https（既有 Web 回跳）
     * 3. 允许自定义 App Scheme（如 myapp://pay/result），供 mobile_app Deep Link
     * 4. 明确拒绝 javascript/data/vbscript/file/blob/about 等危险协议
     */
    const isSafeRedirectUrl = (url = '') => {
      try {
        const parsed = new URL((url || '').trim());
        const protocol = (parsed.protocol || '').toLowerCase();
        if (['http:', 'https:'].includes(protocol)) {
          return true;
        }
        // 危险协议一律拒绝，避免开放重定向被用于 XSS / 本地文件
        const blockedProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'blob:', 'about:'];
        if (blockedProtocols.includes(protocol)) {
          return false;
        }
        // 自定义 Scheme：字母开头，后续允许字母数字 + . -
        return /^[a-z][a-z0-9+.-]*:$/i.test(protocol);
      } catch (error) {
        return false;
      }
    };
    /**
     * 归一化商户回跳地址：保留安全 URL，丢弃不安全或伪相对路径。
     *
     * 业务流程：
     * 1. 空串直接返回
     * 2. 已是安全 URL（含自定义 Scheme）则原样返回
     * 3. 以 /?#\ 开头的相对/伪路径拒绝（避免站内路径劫持）
     * 4. 无协议主机名补 https:// 后再校验（兼容历史缺协议写法）
     */
    const normalizeSafeRedirectUrl = (url = '') => {
      const normalized = (url || '').trim();
      if (!normalized) {
        return '';
      }
      if (isSafeRedirectUrl(normalized)) {
        return normalized;
      }
      if (/^[/?#\\]/.test(normalized)) {
        return '';
      }
      // 已有自定义/非 http(s) Scheme 但未通过安全校验（如 javascript:）→ 直接丢弃，勿再拼 https://
      if (/^[a-z][a-z0-9+.-]*:/i.test(normalized)) {
        return '';
      }
      const candidate = normalized.startsWith('//') ? `https:${normalized}` : `https://${normalized}`;
      return isSafeRedirectUrl(candidate) ? candidate : '';
    };

    const closePayfacTransition = () => {
      showPayfacTransition.value = false;
      showPayfacIframeLoadingMask.value = false;
      payfacTransitionStatus.value = 'PROCESSING';
      payfacTransitionStartedAt.value = 0;
    };

    const openPayfacTransition = (status = 'PROCESSING') => {
      payfacTransitionStatus.value = (status || 'PROCESSING').toString().toUpperCase();
      showPayfacTransition.value = true;
      showPayfacIframeLoadingMask.value = false;
      isOpacity.value = false;
      isLoadingToast.value = false;
      iframeUrl.value = '';
      showIframeUrl.value = false;
      showLoading.value = false;
    };

    const payfacTransitionTitle = computed(() => (payfacTransitionStatus.value === 'PAYMENT_FAIL'
      ? 'Payment verification interrupted'
      : '3D Secure complete'));

    const payfacTransitionMessage = computed(() => (payfacTransitionStatus.value === 'PAYMENT_FAIL'
      ? 'We are syncing the latest payment result. Please stay on this page.'
      : 'We are confirming your payment result. This usually takes a few seconds.'));

    const onPayfacTransitionMessage = (event: MessageEvent) => {
      const payload = event?.data;
      if (!payload || payload.event !== 'UNLIMIT_PAYFAC_3DS_TRANSITION') {
        return;
      }

      const normalizedRedirectUrl = normalizeSafeRedirectUrl(payload.redirectUrl || '');
      if (normalizedRedirectUrl) {
        data.FormData.redirectUrl = normalizedRedirectUrl;
      }

      showPayfacIframeLoadingMask.value = false;
      payfacTransitionStatus.value = (payload.status || 'PROCESSING').toString().toUpperCase();
      if (payfacTransitionStatus.value === 'PAYMENT_SUCCESS' && payfacTransitionStartedAt.value === 0) {
        payfacTransitionStartedAt.value = Date.now();
      }
      isOpacity.value = false;
      isLoadingToast.value = false;
      showLoading.value = false;
      if (isUnlimitPayfacChannel(data.FormData.channelCode)) {
        setTimeout(() => {
          getStatus();
        }, 300);
      }
    };

    const clearCardFormChrome = () => {
      closePayfacTransition();
      endSilentAutoPreOrder();
      isOpacity.value = false;
      isLoadingToast.value = false;
      iframeUrl.value = '';
      showIframeUrl.value = false;
    };

    /** UnLimitPayfac：清 iframe/遮罩，不篡改 orderStatus（由后端 CARDFORM 预下单决定） */
    const activatePayfacCardForm = () => {
      if (!isUnlimitPayfacChannel(data.FormData.channelCode)) {
        return;
      }
      closePayfacTransition();
      clearCardFormChrome();
    };

    const applyPayfacCardFormIfNeeded = (channelCode = '') => {
      const code = channelCode || data.FormData.channelCode;
      if (
        data.FormData.orderStatus === 1
        && isUnlimitPayfacChannel(code)
        && data.FormData.actionType === 'CARDFORM'
      ) {
        activatePayfacCardForm();
        return true;
      }
      return false;
    };

    currentLang.value = lang ?? 'en';
    let data = reactive({
      dev: process.env.VUE_APP_BUILD_ENV,
      token: payToken.value,
      path: window.location.origin,
      InterfaceUI,
      Languages,
      FormData: { ...FormData } as PayinFormData,
      minData: new Date(),
      Search: {
        supplier: '',
        bank: '',
      },
    });

    const showPayfacCardFormPanel = computed(() => {
      if (
        showPayfacCardFormEarly.value
        && isUnlimitPayfacChannel(data.FormData.channelCode)
        && data.FormData.orderStatus === 0
      ) {
        return true;
      }
      return data.FormData.orderStatus === 1 && data.FormData.actionType === 'CARDFORM';
    });

    const isSunpayCashier = computed(
      () => String(data.dev || process.env.VUE_APP_BUILD_ENV || '').includes('sunpay'),
    );

    /** SunPay 冷启动：壳未挂前仅展示 boot loading */
    const isSunpayBootLoading = computed(
      () => isSunpayBuild && isLoadingToast.value && !embedGuardReady.value && !suppressPageReveal.value,
    );

    /**
     * SunPay 首屏就绪后再挂收银台壳，避免冷启动闪选类型页。
     *
     * 业务流程：
     * 1. 仅在 SunPay 构建下生效
     * 2. 拉单/通道/必填项完成后调用
     * 3. 跳转公共页期间不挂壳（suppressPageReveal）
     */
    const revealSunpayCashierShell = () => {
      if (!isSunpayBuild || suppressPageReveal.value) {
        return;
      }
      embedGuardReady.value = true;
    };

    /**
     * SunPay 预下单且为 Payfac 卡收单时，立即进入卡表单区。
     *
     * 业务流程：
     * 1. 校验 SunPay 环境与预下单状态
     * 2. 校验 Payfac 通道
     * 3. 开启 showPayfacCardFormEarly，避免闪「Choose Payment Type」
     */
    const tryActivateSunpayPayfacEarly = () => {
      if (
        !process.env.VUE_APP_BUILD_ENV.includes('sunpay')
        || Number(data.FormData.orderStatus) !== 0
        || !isUnlimitPayfacChannel(data.FormData.channelCode)
      ) {
        return;
      }
      console.info('[payin] SunPay payfac early card form', data.FormData.channelCode);
      showPayfacCardFormEarly.value = true;
    };

    const changeLang = (item) => {
      locale.value = item.value;
    };

    watch(
      currentLang,
      (newLang) => {
        locale.value = newLang;
      },
      {
        immediate: true,
      },
    );
    const getLangName = computed(() => (
        data.Languages.find((x) => x.value === locale.value)?.text || ''
    ));

    // 复制
    const { toClipboard } = useClipboard();
    const copy = async (text: string) => {
      try {
        await toClipboard(text);
        Toast({
          message: t('defray.copied'),
          overlay: false,
          duration: 800,
        });
      } catch (e) {
        console.error(e);
      }
    };

    /**
     * 倒计时结束
     * */
    const isFinish = ref(false);
    const finish = () => {
      isFinish.value = true;
      if (data.FormData.orderStatus == 1) {
        getOrderInfo();
      }
    };

    /**
     * 分割时、分、秒
     * */
    const splitNumber = (number) => {
      const data = number.toString().split('');
      let n1 = 0;
      let n2 = 0;
      if (data.length == 2) {
        n1 = data[0];
        n2 = data[1];
      } else if (data.length == 1) {
        n2 = data[0];
      }
      return [n1, n2];
    };

    /**
     * 获取订单信息；待支付单再拉通道列表。
     *
     * 业务流程：
     * 1. 先拉 payin 订单（支付中/终态只依赖此接口）
     * 2. AP/GP / 卡表单等非待支付态：直接展示或跳转，不再请求 CashChannelList
     * 3. 仅 orderStatus==0 时再拉通道列表与必填项（避免移动端通道接口失败误伤整页）
     */
    const __CashChannelList = ref<Array<ChannelObject>>([]);
    const getChannelList = async () => {
      isLoadingToast.value = true;
      try {
        let orderRes: any;
        try {
          orderRes = await Cashier.GetPayInOrderInfo();
        } catch (e) {
          console.warn('[payin] GetPayInOrderInfo rejected', e);
          isOpacity.value = false;
          Toast({
          message: `${t('cashier.error')}${e && (e as any).message ? ` (${(e as any).message})` : ''}`,
          overlay: false,
          duration: 4000,
        });
          return;
        }

        await getOrderInfo(orderRes);

        if (orderRes?.code != 200) {
          isOpacity.value = false;
          return;
        }

        tryActivateSunpayPayfacEarly();
        // payin 已有金额：Payfac 预下单立刻展示金额（.name 在 showPayfacCardFormEarly 下默认隐藏）
        if (
          showPayfacCardFormEarly.value
          && Number(data.FormData.orderStatus) === 0
          && data.FormData.amount != null
        ) {
          isOpacity.value = false;
        }

        // SunPay 的 Apple Pay / Google Pay：有公共支付页则直接跳转，避免停在空的「Choose Payment Type」
        // 仅待支付/支付中跳转；终态（成功/失败/取消）禁止再跳 Comm，否则与 Comm 失败回跳 cashier 形成死循环刷屏
        const accountType = String(data.FormData?.accountType || orderRes?.data?.accountType || '').toUpperCase();
        const commPageUrl = data.FormData?.commPageUrl || orderRes?.data?.commPageUrl || '';
        const payablCheckoutUrl = String(
          data.FormData?.checkoutUrl || orderRes?.data?.checkoutUrl || '',
        ).trim();
        const apGpOrderStatus = Number(
          data.FormData?.orderStatus ?? orderRes?.data?.orderStatus ?? -1,
        );
        const allowApGpCommRedirect = apGpOrderStatus === 0 || apGpOrderStatus === 1;
        if (
          allowApGpCommRedirect
          && isPayablChannel(data.FormData?.channelCode || orderRes?.data?.channelCode)
          && ['APPLEPAY', 'GOOGLEPAY'].includes(accountType)
          && payablCheckoutUrl
        ) {
          console.info('[payin] Payabl AP/GP top redirect', accountType);
          suppressPageReveal.value = true;
          isLoadingToast.value = true;
          isOpacity.value = true;
          window.top.location.href = payablCheckoutUrl;
          return;
        }
        if (
          allowApGpCommRedirect
          && ['APPLEPAY', 'GOOGLEPAY'].includes(accountType)
          && process.env.VUE_APP_BUILD_ENV.includes('sunpay')
          && commPageUrl
        ) {
          console.info('[payin] AP/GP redirect to common page', accountType, data.FormData.orderStatus);
          suppressPageReveal.value = true;
          isLoadingToast.value = true;
          isOpacity.value = true;
          trackEvent('payment_method', {
            merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
            order_no: data?.FormData?.orderNo,
            button_type: 'COMMON_PAGE',
            event: 'REDIRECT',
            result: 'success',
            path: commPageUrl,
          });
          window.location.href = commPageUrl;
          return;
        }

        // 支付中 / 成功 / 失败 / 关闭：页面已由 getOrderInfo 渲染，无需通道列表
        if (Number(data.FormData.orderStatus) !== 0) {
          isOpacity.value = false;
          return;
        }

        // 仅预下单需要通道列表
        let channelRes: any;
        try {
          channelRes = await Cashier.CashChannelList();
        } catch (e) {
          console.warn('[payin] CashChannelList rejected', e);
          isOpacity.value = false;
          Toast({
            message: t('cashier.error'),
            overlay: false,
            duration: 3000,
          });
          return;
        }

        if (channelRes?.code == 200) {
          __CashChannelList.value = channelRes?.data || [];
          GetChannelListByCoin();
          tryActivateSunpayPayfacEarly();
          // 必须 await：否则 finally 会提前取消透明，先闪出 Sun Pay「Choose Payment Type」再跳卡表单
          await getCashierTopUpRequiredFields();
        } else {
          Toast({
            message: channelRes?.message || t('cashier.error'),
            overlay: false,
            duration: 2000,
          });
          isOpacity.value = false;
        }
      } catch (e) {
        console.warn('[payin] getChannelList unexpected error', e);
        isOpacity.value = false;
        Toast({
          message: t('cashier.error'),
          overlay: false,
          duration: 3000,
        });
      } finally {
        // 跳转公共页 / 自动无感知创单期间不要关掉 loading / 不要挂壳
        if (!suppressPageReveal.value && !silentAutoPreOrder.value) {
          if (isSunpayBuild) {
            revealSunpayCashierShell();
          }
          isLoadingToast.value = false;
        }
      }
    };

    /**
     * 通过币种筛选出支持的通道列表
     */
    const CashChannelList = ref<Array<ChannelObject>>([]);
    const GetChannelListByCoin = () => {
      const list = __CashChannelList.value.filter((x: ChannelObject) => x.cashChannelCurrencyRateSetting.find((channel: ChannelCurrencyRatesObject) => channel?.currency === data.FormData.currency && channel?.flowDirection === 0)) || [];
      CashChannelList.value = list;
      if (list.length) {
        const orderChannel = data.FormData.channelCode;
        const matched = list.find((x: ChannelObject) => x.code === orderChannel);
        data.FormData.channelCode = matched?.code || list[0]?.code;
      }
    };

    /**
     * 通过通道获取对应的支付方式
     */
    const PaymentTypeList = computed(() => {
      if (!data.FormData.channelCode) return;
      const cashChannelCurrencyRateSetting = CashChannelList.value.find((x: ChannelObject) => x.code == data.FormData.channelCode)?.cashChannelCurrencyRateSetting || [];

      let __data = cashChannelCurrencyRateSetting.filter((x) => (
        x.currency == data.FormData.currency && x.flowDirection == 0 && x.supportedChannelType
      )) || [];

      const list: Array<ChannelCurrencyRatesObject> = [];
      const temp = {};
      __data.map((x) => {
        if (!temp[x.supportedChannelType]) {
          temp[x.supportedChannelType] = x.supportedChannelType;
          list.push(x);
        }
      });

      if (list.length > 0) {
        const item = list[0] || {};
        data.FormData.paymentType = item?.supportedChannelType;
        data.FormData.transactionFee = item?.transactionFee || 0;
      }
      // 处理没有任何渠道类型时，费率问题
      else {
        __data = cashChannelCurrencyRateSetting.filter((x) => x.currency == data.FormData.currency && x.flowDirection == 0) || [];
        if (__data.length) {
          data.FormData.transactionFee = __data[0]?.transactionFee || 0;
        }
      }
      return list;
    });

    /**
     * 通过编码查找对应的名称
     * @param code 编码Code
     * @param list 列表数据
     */
    const getNameByCode = (code: string, list: Array<OptionObject>) => (
        list.find((x: OptionObject) => x.value == code)?.key || ''
    );

    // 运营商列表
    const SupplierList = computed(() => {
      const list = __CashChannelList.value.find((x: ChannelObject) => x.code == data.FormData.channelCode)?.cashChannelCurrencyRateSetting || [];

      const temp = list.filter((x) => (
        x.currency == data.FormData.currency && x.flowDirection == 0 && x.supportedMobileType
      )) || [];

      if (temp.length > 1) {
        return (
          temp.map((x) => ({
            name: x.supportedMobileType,
            value: x.supportedMobileType,
          })) || []
        );
      } if (temp.length == 1) {
        const __supportedMobileType = temp[0].supportedMobileType;
        if (Array.isArray(__supportedMobileType)) {
          return (
            __supportedMobileType.map((x) => ({
              name: x,
              value: x,
            })) || []
          );
        }
        return {
          name: __supportedMobileType,
          value: __supportedMobileType,
        };
      }
    });

    let extendFieleds = [];
    const onSelectChange = (val) => {
      if (val.fieldName.includes('bank')) {
        data.FormData.bankName = val.list.find((x) => x.key == val.value.key)?.value || '';
        return;
      }

      const childs = val.list.find((x) => x.key == val.value.key)?.childs || [];

      if (!childs.length) {
        return;
      }

      extendFieleds.map((x) => {
        // @ts-ignore
        data.FormData[x] = '';
        delete data.FormData[x];
        dynamicsFiledList.value.splice(
          // @ts-ignore
          dynamicsFiledList.value.findIndex((f) => f?.key == val.value.key),
          1,
        );
      });

      nextTick(() => {
        extendFieleds = [];
        childs.map((x) => {
          data.FormData[x?.field_name] = '';
          // @ts-ignore
          dynamicsFiledList.value.push(x);
        });
        extendFieleds = childs.map((x) => x.field_name);
      });
    };

    // 银行列表
    const BankList = computed(() => {
      const list = __CashChannelList.value.find((x: ChannelObject) => x.code == data.FormData.channelCode)?.cashChannelCurrencyRateSetting || [];

      let supportBanks = list?.find((x) => (
        x.currency == data.FormData.currency
            && x.flowDirection == 0
            && (['DIRECT', 'BANK', 'SPEI'].includes(x.supportedChannelType)
              || (x.supportedChannelType == 'BANKTRANSFER' && data.FormData.channelCode == 'DukPay'))
      ))?.supportBanks || [];

      // 输入筛选过滤
      if (data.Search.bank) {
        supportBanks = supportBanks.filter((x) => (
          x.bankCode.includes(data.Search.bank)
            || x.bankName.toLocaleLowerCase().includes(data.Search.bank.toLocaleLowerCase())
        ));
      }

      return supportBanks.map((x: SupportedBanksList) => ({
        name: x.bankName,
        value: x.bankCode,
      }));
    });
    const onSelectBank = (val) => {
      showBankSheet.value = false;
      data.FormData.bankCode = val.value;
    };

    /**
     * 获取订单信息。
     *
     * 业务流程：
     * 1. 使用预取结果或再次请求 GetPayInOrderInfo
     * 2. 按订单状态渲染支付中/成功/失败/关闭页
     * 3. 必要时拉起渠道组件或轮询状态
     */
    const showSunpayPay = ref(false);
    const getOrderInfo = async (preloadedRes?: any) => {
      // 外层 getChannelList 已统一控制 loading；单独调用时仍自行开关
      const manageLoading = preloadedRes === undefined;
      const alreadyPaying = Number(data.FormData.orderStatus) === 1;
      // 支付中刷新不要全屏 loading，避免闪圈
      if (manageLoading && !alreadyPaying) {
        isLoadingToast.value = true;
      }
      try {
      const res = preloadedRes !== undefined ? preloadedRes : await Cashier.GetPayInOrderInfo();
      if (res?.code == 200) {
        showSunpayPay.value = false;
        data.FormData = Object.assign(data.FormData, res?.data || {});
        tryActivateSunpayPayfacEarly();
        // 支付中/终态：立刻亮金额区（LCP=div.amount），勿等后续通道/组件
        if (Number(data.FormData.orderStatus) !== 0) {
          isOpacity.value = false;
          isLoadingToast.value = false;
        }
        if (applyPayfacCardFormIfNeeded(res?.data?.channelCode)) {
          isLoadingToast.value = false;
          isOpacity.value = false;
          setTimeout(() => {
            startStatusPolling(0);
          }, 3000);
          return;
        }
        if (data.FormData.orderStatus == 1) {
          const commPageUrl = String(
            res?.data?.commPageUrl || data.FormData.commPageUrl || '',
          ).trim();
          if (
            ['APPLEPAY', 'GOOGLEPAY'].includes(res?.data?.accountType)
            && process.env.VUE_APP_BUILD_ENV.includes('sunpay')
            && commPageUrl
          ) {
            trackEvent('payment_method', {
              merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
              order_no: data?.FormData?.orderNo,
              button_type: 'COMMON_PAGE',
              event: 'REDIRECT',
              result: 'success',
              path: commPageUrl,
            });
            trackEvent('begin_checkout', {
              merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
              order_no: data?.FormData?.orderNo,
              value: data?.FormData?.amount,
              currency: data?.FormData?.currency,
            });
            suppressPageReveal.value = true;
            isLoadingToast.value = true;
            isOpacity.value = true;
            window.location.replace(commPageUrl);
            // showSunpayPay.value = true;
          } else {
            isOpacity.value = false;
            if (res?.data?.actionType === 'COMPONENT') {
              // loadingToast?.close();
              isLoadingToast.value = false;
              const channelCode = String(res?.data?.channelCode || '').toLowerCase();
              if (channelCode.includes('nuvei')) {
                NuveiWebComponentsInit(
                  res?.data?.paymentSessionObject,
                  res?.data?.merchantSiteId,
                  res?.data?.merchantNo,
                );
              } else if (channelCode === 'checkout') {
                CheckoutWebComponentsInit(
                  res?.data?.paymentSessionObject,
                  res?.data?.paymentSessionPublicKey,
                );
              }
            } else if (res?.data?.actionType === 'CARDFORM') {
              clearCardFormChrome();
            } else if (isUnlimitPayfacChannel(res?.data?.channelCode)) {
              activatePayfacCardForm();
            }
          }
          // loadingToast?.close();
          isLoadingToast.value = false;
          setTimeout(() => {
            startStatusPolling(0);
          }, 3000);
        } else {
          switch (Number(data.FormData.orderStatus)) {
            // Success
            case 2:
              isOpacity.value = false;
              if (localStorage.getItem(data?.FormData?.orderNo)) {
                const endTime = new Date().getTime();
                const startTime = Number(localStorage.getItem(data?.FormData?.orderNo));
                if (startTime && endTime) {
                  trackEvent('order_transaction_time', {
                    merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
                    order_no: data?.FormData?.orderNo,
                    currency: data?.FormData?.currency,
                    amoount: data?.FormData?.amount,
                    payment_method: data?.FormData?.accountType,
                    time: (endTime - startTime) / 1000 || null,
                  });
                  localStorage.removeItem(data?.FormData?.orderNo);
                }
              }

              setTimeout(() => {
                paymentUrl(data.FormData.redirectUrl, 'SUCCESS');
              }, 400);
              break;
            // Fail
            case 3:
            case 5:
            case 6:
              isOpacity.value = false;
              if (localStorage.getItem(data?.FormData?.orderNo)) {
                const endTime = new Date().getTime();
                const startTime = Number(localStorage.getItem(data?.FormData?.orderNo));
                if (startTime && endTime) {
                  trackEvent('order_transaction_time', {
                    merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
                    order_no: data?.FormData?.orderNo,
                    time: (endTime - startTime) / 1000 || null,
                  });
                  localStorage.removeItem(data?.FormData?.orderNo);
                }
              }
              paymentUrl(data.FormData.cancelUrl, 'FAIL');
              break;
            // Cancel
            case 4:
              isOpacity.value = false;
              paymentUrl(data.FormData.cancelUrl, 'CANCEL');
              break;
          }
        }
        observer();
      } else {
        Toast({
          message: res?.message || t('error'),
          overlay: false,
          duration: 2000,
        });
      }
      } catch (e) {
        console.warn('[payin] GetPayInOrderInfo failed', e);
        isOpacity.value = false;
        Toast({
          message: t('cashier.error'),
          overlay: false,
          duration: 3000,
        });
      } finally {
        if (manageLoading) {
          isLoadingToast.value = false;
        }
      }
    };

    // 订单状态轮询：全局只保留一个 timer
    let statusPollTimer: ReturnType<typeof setTimeout> | null = null;
    /**
     * 安排下一次状态查询（取消上一次未触发的 timer）。
     *
     * 业务流程：
     * 1. clear 已有 timer
     * 2. delay ms 后执行 getStatus
     */
    const scheduleStatusPoll = (delayMs: number) => {
      if (statusPollTimer) {
        clearTimeout(statusPollTimer);
      }
      statusPollTimer = setTimeout(() => {
        statusPollTimer = null;
        getStatus();
      }, delayMs);
    };
    /**
     * 启动/刷新订单状态轮询。
     *
     * 业务流程：
     * 1. 单一定时器调度 getStatus
     * 2. 保证同时只有一条轮询链
     */
    const startStatusPolling = (delayMs = 0) => {
      scheduleStatusPoll(delayMs);
    };
    // 轮询获取状态
    const getStatus = async () => {
      const res = await Cashier.GetPayInOrderInfo();
      if (res?.code == 200) {
        data.FormData = Object.assign(data.FormData, res?.data || {});
        switch (data.FormData.orderStatus) {
          // Pending
          case 1:
            scheduleStatusPoll(5000);
            break;
          // Success
          case 2:
            trackEvent('payment_status', {
              merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
              order_no: data?.FormData?.orderNo,
              event: 'STATUS_CHANGE',
              result: 'success',
            });
            isOpacity.value = false;
            if (localStorage.getItem(data?.FormData?.orderNo)) {
              const endTime = new Date().getTime();
              const startTime = Number(localStorage.getItem(data?.FormData?.orderNo));
              if (startTime && endTime) {
                trackEvent('order_transaction_time', {
                  merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
                  order_no: data?.FormData?.orderNo,
                  time: (endTime - startTime) / 1000,
                });
                localStorage.removeItem(data?.FormData?.orderNo);
              }
            }
            const redirectDelayMs = resolvePayfacSuccessRedirectDelayMs({
              isPayfac: isUnlimitPayfacChannel(data.FormData.channelCode),
              transitionStatus: payfacTransitionStatus.value,
              transitionStartedAt: payfacTransitionStartedAt.value,
              nowMs: Date.now(),
              minDisplayMs: payfacSuccessMinDisplayMs,
              baseDelayMs: 400,
            });
            setTimeout(() => {
              paymentUrl(data.FormData.redirectUrl, 'SUCCESS');
            }, Math.max(redirectDelayMs, 0));
            break;
          // Fail
          case 3:
          case 5:
          case 6:
            if (statusPollTimer) {
              clearTimeout(statusPollTimer);
              statusPollTimer = null;
            }
            // 已由 onResult 关死并跳转时，不再重复 paymentUrl
            if (nuveiCheckoutFailHandled) {
              break;
            }
            nuveiCheckoutFailHandled = true;
            destroyNuveiCheckoutUi();
            trackEvent('payment_status', {
              merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
              order_no: data?.FormData?.orderNo,
              event: 'STATUS_CHANGE',
              result: 'fail',
            });
            if (localStorage.getItem(data?.FormData?.orderNo)) {
              const endTime = new Date().getTime();
              const startTime = Number(localStorage.getItem(data?.FormData?.orderNo));
              if (startTime && endTime) {
                trackEvent('order_transaction_time', {
                  merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
                  order_no: data?.FormData?.orderNo,
                  time: (endTime - startTime) / 1000 || null,
                });
                localStorage.removeItem(data?.FormData?.orderNo);
              }
            }
            isOpacity.value = false;
            paymentUrl(data.FormData.cancelUrl, 'FAIL');
            break;
          // Cancel
          case 4:
            trackEvent('payment_status', {
              merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
              order_no: data?.FormData?.orderNo,
              event: 'STATUS_CHANGE',
              result: 'cancel',
            });
            isOpacity.value = false;
            paymentUrl(data.FormData.cancelUrl, 'CANCEL');
            break;
        }
        observer();
      } else {
        scheduleStatusPoll(5000);
      }
    };

    let ob;
    const showDwonMore = ref(false);
    const observer = () => {
      nextTick(() => {
        setTimeout(() => {
          const last = document.querySelector('#last');
          if (last && !ob) {
            ob = new IntersectionObserver(
              (e) => {
                if (e[0].isIntersecting) {
                  showDwonMore.value = false;
                } else {
                  showDwonMore.value = true;
                }
              },
              {
                root: null,
                threshold: 0.1,
              },
            );
            ob.observe(last);
          }
        }, 200);
      });
    };

    /**
     * 付款路由操作行为
     * */
    const status = {
      SUCCESS: 'CASHIER-TOPUP-SUCCESS',
      FAIL: 'CASHIER-TOPUP-FAIL',
      CANCEL: 'CASHIER-TOPUP-CANCEL',
    };
    const paymentUrl = (url, type) => {
      closePayfacTransition();
      showIframeUrl.value = false;
      iframeUrl.value = '';
      const normalizedUrl = normalizeSafeRedirectUrl(url);
      if (from == 'app') {
        window['BacktrackToApp']?.postMessage(
          JSON.stringify({
            key: status[type],
            data: null,
          }),
        );
        return;
      }
      if (window.parent && window !== window.parent) {
        window?.parent?.postMessage(
          {
            event: status[type],
            data: null,
          },
          '*',
        );
        return;
      }

      if (normalizedUrl && window.location.href !== normalizedUrl) {
        window.location.href = normalizedUrl;
      }
    };

    // 日历控件回调
    const onDateTimePickerHandler = (val) => {
      const year = new Date(val).getFullYear().toString();
      let month = (new Date(val).getMonth() + 1).toString();
      if (month.length < 2) {
        month = `0${month}`;
      }
      data.FormData.expiryDate = `${month}/${year.substring(year.length - 2)}`;
      showCalendar.value = false;
    };
    const onConfirmDate = (date) => {
      // data.FormData.expiryDate = `${date.getMonth() + 1}/${date.getDate()}`;
      showCalendar.value = false;
    };

    /**
     * 上一步、下一步
     */
    const step = ref(1);
    const steps = ref(1);
    const nextStep = (val: boolean) => {
      if (val) {
        if (step.value == steps.value) {
          CreateTopUpOrder();
        } else {
          step.value += 1;
        }
      } else {
        step.value -= 1;
      }
    };

    // 下一步按钮禁用逻辑
    const isDisableConfirm = computed(() => {
      let result = false;
      dynamicsFiledList.value.map((x) => {
        // @ts-ignore
        if (!data.FormData[x?.field_name]) {
          result = true;
        }
      });
      return result;
    });

    /**
     * 创建第三方支付订单
     */
    const LinkedPayForm = ref();
    const NuveiForm = ref();
    const iframeUrl = ref('');
    const CreateTopUpOrder = async () => {
      if (isDisableConfirm.value) {
        // 自动无感知创单若因校验被挡，需揭开遮罩，避免一直 loading
        if (silentAutoPreOrder.value) {
          autoPreOrderHidePaytype.value = false;
          endSilentAutoPreOrder();
          isOpacity.value = false;
          isLoadingToast.value = false;
        }
        return;
      }
      const param: any = {
        device: isMobile() ? 'mobile' : 'pc',
      };
      dynamicsFiledList.value.map((x) => {
        // @ts-ignore
        const value = data.FormData[x?.field_name].trim();
        // @ts-ignore
        param[x?.field_name] = `${x?.expand ? x?.expand : ''}${value}`;
      });
      // const loadingToast = Toast({
      //   message: "",
      //   type: "loading",
      //   overlay: true,
      //   duration: 0
      // });
      isLoadingToast.value = true;
      localStorage.setItem(data.FormData.orderNo, new Date().getTime().toString());
      try {
      // Nuvei AP/GP 等走 CreatePayIn 建单，此处上报 WeTech（卡支付另在 CreateCardPay）
      // 成功带 device_id；失败不传字段、不传错误信息
      const wetechPayload = await getWetechDevicePayload();
      if (wetechPayload.device_id) {
        param.device_id = wetechPayload.device_id;
      }
      const res = await Cashier.CreatePayIn(param);
      if (res?.code == 200) {
        trackEvent('add_to_cart', {
          merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
          order_no: data?.FormData?.orderNo,
          value: data?.FormData?.amount,
          currency: data?.FormData?.currency,
        });
        data.FormData = Object.assign(data.FormData, res?.data || {});
        data.FormData.extend = res?.data || {};
        if (res?.data?.orderStatus != null || res?.data?.order_status != null) {
          data.FormData.orderStatus = res?.data?.orderStatus ?? res?.data?.order_status;
        }
        if (res?.data?.actionType || res?.data?.action_type) {
          data.FormData.actionType = res?.data?.actionType ?? res?.data?.action_type;
        }
        data.FormData.thirdPlatformAmount = res?.data?.thirdPlatformAmount;
        data.FormData.responseDescription = res?.data?.responseDescription;
        // 静默态延后到各分支目标 UI 就绪再结束，避免选类型/空白闪屏
        const {
          checkoutUrl,
          challengeRequired,
          responseCode,
          responseDescription,
          actionType,
          paymentSessionObject,
          paymentSessionPublicKey,
          commPageUrl,
        } = res?.data;
        const sunpayCommPageUrl = String(
          commPageUrl || data.FormData.commPageUrl || '',
        ).trim();

        if (
          ['APPLEPAY', 'GOOGLEPAY'].includes(res?.data?.accountType)
          && process.env.VUE_APP_BUILD_ENV.includes('sunpay')
          && sunpayCommPageUrl
        ) {
          // 仅在公共页地址有效时隐藏收银台并跳转；空地址必须继续走组件/状态页。
          suppressPageReveal.value = true;
          isLoadingToast.value = true;
          isOpacity.value = true;
          endSilentAutoPreOrder();
          window.location.replace(sunpayCommPageUrl);
        }
        // Flow 组件交互
        else if (actionType === 'COMPONENT') {
          setTimeout(() => {
            const channelCode = String(res?.data?.channelCode || '').toLowerCase();
            if (channelCode.includes('nuvei')) {
              NuveiWebComponentsInit(
                res?.data?.paymentSessionObject,
                res?.data?.merchantSiteId,
                res?.data?.merchantNo,
              );
            } else if (channelCode === 'checkout') {
              CheckoutWebComponentsInit(paymentSessionObject, paymentSessionPublicKey);
            }
            // 组件挂上后再揭开，避免空白闪屏
            revealAfterSilentTargetReady();
            getOrderInfo();
          }, 200);
        }
        // CARDFORM：Checkout / Nuvei / UnLimitPayfac 内嵌卡表单
        else if (actionType === 'CARDFORM') {
          clearCardFormChrome();
          setTimeout(() => {
            getStatus();
          }, 3000);
        } else if (isUnlimitPayfacChannel(data.FormData.channelCode || res?.data?.channelCode)) {
          activatePayfacCardForm();
          setTimeout(() => {
            getStatus();
          }, 3000);
        } else if (
          checkoutUrl
          && !isUnlimitPayfacChannel(data.FormData.channelCode || res?.data?.channelCode)
        ) {
          // 表单提交
          if (data.FormData.channelCode == 'LinkedPay') {
            await Cashier.GetFiatOrderLinkUsed();
            setTimeout(() => {
              showIframeUrl.value = true;
              LinkedPayForm.value.submit();
              setTimeout(() => {
                getOrderInfo();
              }, 200);
              revealAfterSilentTargetReady();
            }, 200);
          }
          // Nuvei 渠道表单提交
          else if (challengeRequired) {
            setTimeout(() => {
              showIframeUrl.value = true;
              NuveiForm.value.submit();
              setTimeout(() => {
                getOrderInfo();
              }, 200);
              revealAfterSilentTargetReady();
            }, 200);
          }
          // 跳转：先挂目标再关 loading
          else {
            showIframeUrl.value = false;
            const channelCode = data.FormData.channelCode || res?.data?.channelCode;
            const accountType = data.FormData.accountType || res?.data?.accountType;
            if (openPayablTopRedirectIfNeeded(checkoutUrl, channelCode, accountType)) {
              return;
            }
            if (data.FormData.channelCode === 'DukPay') {
              window.open(checkoutUrl, '_blank');
            }
            if (data.FormData.channelCode === 'DusuPayV2' && data.FormData.currency === 'ZAR') {
              window.open(checkoutUrl, '_self');
            } else {
              iframeUrl.value = checkoutUrl;
            }
            revealAfterSilentTargetReady();
            setTimeout(() => {
              getOrderInfo();
            }, 100);
          }
          RedirectURL();
        } else if (responseCode?.toLowerCase() == 'failed') {
          revealAfterSilentTargetReady();
          Toast({
            message: responseDescription,
            overlay: false,
            duration: 5000,
          });
        } else if (
          ['AlchemyPay'].includes(data.FormData.channelCode)
            && ['QRIS', 'OVO', 'DIRECT'].includes(data.FormData.paymentType)
        ) {
          revealAfterSilentTargetReady();
          result.value = true;
        } else {
          // autoPreOrderHidePaytype 仍生效，避免 orderStatus 仍为 0 时选类型回流
          revealAfterSilentTargetReady();
          getOrderInfo();
        }
      } else {
        autoPreOrderHidePaytype.value = false;
        endSilentAutoPreOrder();
        // loadingToast.close();
        isLoadingToast.value = false;
        Toast({
          message: res?.message || t('cashier.error'),
          overlay: false,
          duration: 10000,
        });

        // 创单失败，未生成订单，页面如何显示? --- 2024/12/20 15:00:00 张志安确认修改
        isOpacity.value = false;
        data.FormData.orderStatus = 3;
      }
      } catch (e) {
        console.warn('[payin] CreatePayIn failed', e);
        autoPreOrderHidePaytype.value = false;
        endSilentAutoPreOrder();
        isLoadingToast.value = false;
        isOpacity.value = false;
        Toast({
          message: t('cashier.error'),
          overlay: false,
          duration: 10000,
        });
      }
    };

    // 监听第三方支付平台完成了操作，监听执行跳转的逻辑
    // 跳出iframe回到下游业务系统指定页面
    const RedirectURL = () => {
      setTimeout(() => {
        if (showIframeUrl.value) {
          let timeStamp = 0;
          const myIframe = (document.getElementById('myIframe') as HTMLIFrameElement | null)
            || document.createElement('iframe');
          myIframe.addEventListener('load', (e) => {
            const normalizedRedirectUrl = normalizeSafeRedirectUrl(data.FormData.redirectUrl);
            if (timeStamp == 0) {
              timeStamp = e.timeStamp;
            }
            if (e.timeStamp >= timeStamp && timeStamp !== 0) {
              // console.log(myIframe?.src, data.FormData.redirectUrl);
              // @ts-ignore
              if (normalizedRedirectUrl && myIframe?.src == normalizedRedirectUrl) {
                window.location.href = normalizedRedirectUrl;
              }
              // @ts-ignore
              if (myIframe?.src == window.location.href) {
                if (normalizedRedirectUrl) {
                  window.location.href = normalizedRedirectUrl;
                } else if ([2, 3, 4].includes(data.FormData.orderStatus)) {
                  showIframeUrl.value = false;
                }
              }

              try {
                // @ts-ignore
                const currentUrl = myIframe.contentWindow.location.href;
                console.log('iframe 最新完整地址：', currentUrl);
                showPayfacIframeLoadingMask.value = shouldShowPayfacIframeLoadingMask({
                  isPayfac: isUnlimitPayfacChannel(data.FormData.channelCode),
                  currentUrl,
                  iframeSrc: myIframe?.src,
                });
                if (currentUrl == window.location.href) {
                  iframeUrl.value = '';
                  showLoading.value = true;
                }
              } catch (err) {
                // @ts-ignore
                console.log('跨域，无法直接读取 location.href，可能不是最终地址）:', myIframe.src);
                showPayfacIframeLoadingMask.value = shouldShowPayfacIframeLoadingMask({
                  isPayfac: isUnlimitPayfacChannel(data.FormData.channelCode),
                  currentUrl: '',
                  iframeSrc: myIframe?.src,
                });
                // @ts-ignore
                if (myIframe.src == window.location.href) {
                  iframeUrl.value = '';
                  showLoading.value = true;
                }
              }
            }
          });
        }
      }, 1000);
    };

    const showIframeUrl = ref(false);
    const showLoading = ref(false);

    /**
     * Payabl 渠道顶层跳转（卡 3DS / AP / GP）；非 Payabl 返回 false，其它渠道逻辑不变。
     *
     * 业务流程：
     * 1. 校验 URL 与 channelCode 是否命中 Payabl 规则
     * 2. 展示 loading 并通过 window.top 打开目标页
     * 3. 返回 true 表示已处理，调用方应直接 return
     */
    const openPayablTopRedirectIfNeeded = (
      checkoutUrl: string,
      channelCode = '',
      accountType = '',
      openMode = '',
    ) => {
      const targetUrl = String(checkoutUrl || '').trim();
      if (!targetUrl || !shouldTopOpenPayablRedirect(channelCode, accountType, openMode)) {
        return false;
      }
      suppressPageReveal.value = true;
      isLoadingToast.value = true;
      window.top.location.href = targetUrl;
      return true;
    };

    const toPay = async () => {
      if (isUnlimitPayfacChannel(data.FormData.channelCode)) {
        activatePayfacCardForm();
        return;
      }
      if (data.FormData.channelCode == 'LinkedPay') {
        const res = await Cashier.GetFiatOrderLinkUsed();
        if (res?.code == 200) {
          if (!res?.data) {
            showIframeUrl.value = true;
            LinkedPayForm.value.submit();
          } else {
            LinkedPayForm.value.remove();
            Dialog({
              title: t('tips'),
              message: t('used'),
              confirmButtonText: t('yes'),
            });
          }
        }
      } else {
        showIframeUrl.value = false;
        if (
          openPayablTopRedirectIfNeeded(
            data.FormData.checkoutUrl,
            data.FormData.channelCode,
            data.FormData.accountType,
          )
        ) {
          return;
        }
        if (data.FormData.channelCode === 'DukPay') {
          window.open(data.FormData.checkoutUrl, '_blank');
        } else if (data.FormData.channelCode === 'DusuPayV2' && data.FormData.currency === 'ZAR') {
          window.open(data.FormData.checkoutUrl, '_self');
        } else if (data.FormData.challengeRequired) {
          showIframeUrl.value = true;
          NuveiForm.value.submit();
          showLoading.value = false;
        } else {
          if (showLoading.value) {
            showIframeUrl.value = true;
          } else {
            iframeUrl.value = data.FormData.checkoutUrl;
          }
          RedirectURL();
        }
      }
    };

    const dynamicsFiledList = ref<DynamicsFieldItem[]>([]);
    const getCashierTopUpRequiredFields = async () => {
      isLoadingToast.value = true;
      try {
      const res = await Cashier.CashierTopUpRequiredFields();
      if (res?.code == 200) {
        const temp = res?.data || [];
        const list = temp.filter(
          (x) => !['redirect_url', 'user_ip', 'device'].includes(x.field_name),
        );
        // Pre-sort "Supplier" and "BankCode" field
        list.unshift(
          ...list.splice(
            list.findIndex((item) => ['bank_code', 'supplier'].includes(item.field_name)),
            1,
          ),
        );
        dynamicsFiledList.value = list;
        dynamicsFiledList.value.map((x) => {
          // @ts-ignore
          data.FormData[x?.field_name] = '';
        });

        // 有需要填写的动态表单自动跳转到第二步，不停留在第一步
        steps.value = dynamicsFiledList.value.length ? 2 : 1;
        // UnLimitPayfac 无动态字段：禁止自动创单，等用户填卡后 Pay
        if (isUnlimitPayfacChannel(data.FormData.channelCode)) {
          showPayfacCardFormEarly.value = true;
          isOpacity.value = false;
          return;
        }
        if (isSunpayBuild) {
          showPayfacCardFormEarly.value = false;
        }
        const isSunpay = isSunpayBuild;
        if (dynamicsFiledList.value.length) {
          // SunPay 不展示选类型步，直接露出填单区
          if (isSunpay) {
            step.value = steps.value;
            isOpacity.value = false;
          } else {
            nextStep(true);
            setTimeout(() => {
              isOpacity.value = false;
            }, 400);
          }
        } else if (isSunpay) {
          // SunPay 保持原有自动创单流程，不启用 Klickl 的静默展示状态；
          // 否则 getChannelList 会因 silentAutoPreOrder 跳过收银台壳挂载。
          await CreateTopUpOrder();
        } else {
          // Klickl 无动态字段：自动创单，并在展示上跳过预下单选择页
          beginSilentAutoPreOrder();
          nextStep(true);
        }
      } else {
        isOpacity.value = false;
        Toast({
          message: t('cashier.error'),
          overlay: false,
          duration: 3000,
        });
      }
      } catch (e) {
        console.warn('[payin] CashierTopUpRequiredFields failed', e);
        autoPreOrderHidePaytype.value = false;
        endSilentAutoPreOrder();
        isOpacity.value = false;
        Toast({
          message: t('cashier.error'),
          overlay: false,
          duration: 3000,
        });
      } finally {
        // 自动无感知创单期间由 CreateTopUpOrder 收尾，勿提前关掉 loading
        if (!isSunpayBuild && !silentAutoPreOrder.value) {
          isLoadingToast.value = false;
        }
      }
    };
    const themes = {
      light: {
        colorBackground: '#ffffff',
        colorBorder: '#CED0D1',
        colorPrimary: '#09182B',
        colorSecondary: '#828687',
        colorAction: '#000000',
        colorOutline: '#FFFFFF',
        colorSuccess: '#3CB628',
        colorError: '#8B3232',
        colorDisabled: '#AAAAAA',
        colorInverse: '#ffffff',
        colorFormBackground: '#F5F5F5',
        colorFormBorder: '#F5F5F5',
        borderRadius: ['0px', '0px'],
        subheading: {
          fontFamily:
            "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 500,
          letterSpacing: 0,
        },
        footnote: {
          fontFamily:
            "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 400,
          letterSpacing: 0,
        },
        button: {
          fontFamily:
            "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 500,
          letterSpacing: 0,
        },
        input: {
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '16px',
          lineHeight: '20px',
          fontWeight: 400,
          letterSpacing: 0,
        },
        label: {
          fontFamily:
            "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 500,
          letterSpacing: 0,
        },
      },
      dark: {
        colorBackground: '#0A0A0C',
        colorBorder: '#68686C',
        colorPrimary: '#F9F9FB',
        colorSecondary: '#828388',
        colorAction: '#5E48FC',
        colorOutline: '#ADA4EC',
        colorSuccess: '#2ECC71',
        colorError: '#FF3300',
        colorDisabled: '#64646E',
        colorInverse: '#F9F9FB',
        colorFormBackground: '#1F1F1F',
        colorFormBorder: '#1F1F1F',
        borderRadius: ['8px', '8px'],
        subheading: {
          fontFamily:
            "'Roboto Mono', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 700,
          letterSpacing: 0,
        },
        footnote: {
          fontFamily:
            "'PT Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 400,
          letterSpacing: 0,
        },
        button: {
          fontFamily:
            "'Roboto Mono', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 700,
          letterSpacing: 0,
        },
        input: {
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '16px',
          lineHeight: '20px',
          fontWeight: 400,
          letterSpacing: 0,
        },
        label: {
          fontFamily:
            "'Roboto Mono', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Noto Sans', 'Liberation Sans', Arial, sans-serif;",
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 400,
          letterSpacing: 0,
        },
      },
    };
    const initCheckoutWebComponents = ref(false);
    /**
     * 初始化 Checkout.com 组件（按需加载 SDK）。
     *
     * 业务流程：
     * 1. 动态加载 Checkout Web Components 脚本
     * 2. 用 paymentSession 创建 flow 组件并挂载
     */
    const CheckoutWebComponentsInit = async (paymentSessionObject, paymentSessionPublicKey) => {
      if (initCheckoutWebComponents.value) return;
      trackEvent('payment_method', {
        merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
        order_no: data?.FormData?.orderNo,
        button_type: data.FormData?.accountType,
        event: 'render',
        result: 'success',
      });
      const CheckoutWebComponents = await ensureCheckoutWebComponentsReady();
      const checkout = await CheckoutWebComponents({
        paymentSession: JSON.parse(paymentSessionObject),
        publicKey: paymentSessionPublicKey,
        environment: ['prod', 'sunpayprod'].includes(process.env.VUE_APP_BUILD_ENV)
          ? 'production'
          : 'sandbox',
        appearance: themes.light,
        componentOptions: {
          googlepay: {
            buttonType: 'plain',
            buttonColor: 'white',
          },
        },
      });
      const flowComponent = checkout.create('flow');
      nextTick(() => {
        flowComponent.mount('#flow-container');
        initCheckoutWebComponents.value = true;
      });
    };

    /**
     * 初始化 Nuvei 组件（按需加载 SafeCharge SDK）。
     *
     * 业务流程：
     * 1. 同一 sessionToken 只 init 一次，避免轮询/onResult 反复重建导致闪圈
     * 2. 动态加载 checkout.js（失败则打日志并结束 loading）
     * 3. 按支付方式配置 ApplePay / GooglePay 白名单并挂载
     * 4. 打印 checkout 入参便于排查；billingAddress 含 email/country/firstName/lastName
     */
    /**
     * 生成 checkout 专用 clientRequestId（Nuvei 幂等键，每次 checkout 必须唯一）。
     *
     * 业务流程：
     * 1. 优先 crypto.randomUUID
     * 2. 其次 crypto.getRandomValues 生成 16 字节 hex
     * 3. 最后用时间戳 + 补齐随机数兜底，保证固定长度
     * 4. 拼接 orderNo 与 _checkout，避免与 openOrder 的 _open 冲突
     */
    const buildCheckoutClientRequestId = (orderNo = '') => {
      let uuid = '';
      type CryptoWithRandomUUID = Crypto & { randomUUID?: () => string };
      const webCrypto = typeof crypto !== 'undefined'
        ? (crypto as CryptoWithRandomUUID)
        : undefined;
      if (webCrypto && typeof webCrypto.randomUUID === 'function') {
        uuid = webCrypto.randomUUID();
      } else if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        uuid = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      } else {
        const timePart = Date.now().toString(16).padStart(12, '0');
        const randPart = Math.floor(Math.random() * 1e16).toString(16).padStart(14, '0');
        uuid = `${timePart}${randPart}`;
      }
      const normalizedOrderNo = `${orderNo || ''}`.trim();
      return normalizedOrderNo ? `${uuid}_${normalizedOrderNo}_checkout` : `${uuid}_checkout`;
    };

    let nuveiCheckoutSessionToken = '';
    /** 同一会话内失败终态只处理一次，避免 destroy / 失败跳转重复 */
    let nuveiCheckoutFailHandled = false;

    /**
     * 销毁 Nuvei Simply Connect 组件，阻止同 session 换卡再付。
     *
     * 业务流程：
     * 1. 调用 checkout.destroy() 卸掉组件
     * 2. 清空 #flow-container 兜底
     * 3. 清空 session 标记
     */
    const destroyNuveiCheckoutUi = () => {
      try {
        // @ts-ignore
        if (typeof window.checkout?.destroy === 'function') {
          // @ts-ignore
          window.checkout.destroy();
        }
      } catch (e) {
        console.warn('[checkout] destroy failed', e);
      }
      const el = document.querySelector('#flow-container');
      if (el) {
        el.innerHTML = '';
      }
      nuveiCheckoutSessionToken = '';
    };

    /**
     * Nuvei 组件侧失败：关死组件并走与 orderStatus=3 相同的失败页逻辑。
     *
     * 业务流程：
     * 1. 幂等：已处理过则直接返回
     * 2. destroy 组件并停止状态轮询
     * 3. 本地切到失败态（隐藏 flow 区）
     * 4. 埋点 + paymentUrl(cancelUrl, FAIL)，与 getStatus case 3/5/6 一致
     */
    const handleNuveiCheckoutFailLikeOrderStatus3 = () => {
      if (nuveiCheckoutFailHandled) {
        return;
      }
      nuveiCheckoutFailHandled = true;
      destroyNuveiCheckoutUi();
      if (statusPollTimer) {
        clearTimeout(statusPollTimer);
        statusPollTimer = null;
      }
      data.FormData.orderStatus = 3;
      isOpacity.value = false;
      isLoadingToast.value = false;
      trackEvent('payment_status', {
        merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
        order_no: data?.FormData?.orderNo,
        event: 'STATUS_CHANGE',
        result: 'fail',
      });
      if (localStorage.getItem(data?.FormData?.orderNo)) {
        const endTime = new Date().getTime();
        const startTime = Number(localStorage.getItem(data?.FormData?.orderNo));
        if (startTime && endTime) {
          trackEvent('order_transaction_time', {
            merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
            order_no: data?.FormData?.orderNo,
            time: (endTime - startTime) / 1000 || null,
          });
          localStorage.removeItem(data?.FormData?.orderNo);
        }
      }
      paymentUrl(data.FormData.cancelUrl, 'FAIL');
    };

    const NuveiWebComponentsInit = async (paymentSessionObject, merchantSiteId, merchantNo) => {
      if (nuveiCheckoutFailHandled) {
        return;
      }
      if (
        paymentSessionObject
        && nuveiCheckoutSessionToken
        && nuveiCheckoutSessionToken === paymentSessionObject
      ) {
        return;
      }
      let checkout: any;
      try {
        checkout = await ensureNuveiCheckoutReady();
      } catch (e) {
        console.warn('[checkout] SDK load failed', e);
        isLoadingToast.value = false;
        return;
      }
      nuveiCheckoutSessionToken = paymentSessionObject || '';
      trackEvent('payment_method', {
        merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
        order_no: data?.FormData?.orderNo,
        button_type: data.FormData?.accountType,
        event: 'render',
        result: 'success',
      });
      nextTick(() => {
        const pmWhitelist: string[] = [];
        if (data.FormData?.accountType == 'APPLEPAY') {
          pmWhitelist.push('ppp_ApplePay');
        } else if (data.FormData?.accountType == 'GOOGLEPAY') {
          pmWhitelist.push('ppp_GooglePay');
        }

        const email = data?.FormData?.emailAddress || '';
        const billingCountry =
          data?.FormData?.countryOfBillingAddress
          || data?.FormData?.billingAddressCountryCode
          || data?.FormData?.country
          || 'US';
        // 仅下游有传名字时拆分；不传则不填 firstName/lastName（非 KYC，禁止用邮箱/Customer 乱填）
        const fullNameRaw = `${data?.FormData?.accountName || data?.FormData?.customerName || ''}`.trim();
        const nameParts = fullNameRaw.split(/\s+/).filter(Boolean);
        const billingFirstName = nameParts[0] || '';
        const billingLastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
        const checkoutClientUniqueId = data?.FormData?.clientUniqueId || data?.FormData?.orderNo || '';
        const checkoutClientRequestId = buildCheckoutClientRequestId(data?.FormData?.orderNo);

        /**
         * Nuvei checkout 支付结果回调。
         *
         * 业务流程：
         * 1. 记录结果摘要（不含敏感卡号）并上报
         * 2. DECLINED/ERROR/取消：关死组件并走与 orderStatus=3 相同失败跳转
         * 3. 其它结果：刷新订单状态轮询
         */
        const onNuveiCheckoutResult = (result: any) => {
          const status = result?.result || result?.transactionStatus || '';
          const statusUpper = String(status).toUpperCase();
          console.info('checkout onResult', {
            orderNo: data?.FormData?.orderNo,
            status,
            errCode: result?.errCode,
            errorDescription: result?.errorDescription,
            transactionId: result?.transactionId,
            cancelled: result?.cancelled,
          });
          Cashier.ReportNuveiCheckoutResult({
            order_no: data?.FormData?.orderNo,
            payment_type: data.FormData?.accountType,
            client_unique_id: checkoutClientUniqueId,
            client_request_id: checkoutClientRequestId,
            result: status,
            err_code: result?.errCode ?? result?.errorCode,
            error_description: result?.errorDescription || result?.reason || result?.message,
            transaction_id: result?.transactionId,
            cancelled: !!result?.cancelled,
            gw_error_code: result?.gwErrorCode,
            gw_extended_error_code: result?.gwExtendedErrorCode,
          });
          trackEvent('payment_method', {
            merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
            order_no: data?.FormData?.orderNo,
            button_type: data.FormData?.accountType,
            event: 'onResult',
            result: status || 'unknown',
          });
          const isTerminalFail =
            ['DECLINED', 'ERROR', 'FAIL'].includes(statusUpper) || !!result?.cancelled;
          if (isTerminalFail) {
            handleNuveiCheckoutFailLikeOrderStatus3();
            return;
          }
          setTimeout(() => {
            // 支付结果回来立刻查一次；单一定时器，不会叠多条轮询
            startStatusPolling(0);
          }, 500);
        };

        const params = {
          logLevel: 0,
          payButton: 'amountButton',
          showResponseMessage: true,
          // 关掉 Decline Recovery 引导，失败由我们 destroy + 失败页收口
          disableDeclineRecovery: true,
          sessionToken: paymentSessionObject,
          // clientUniqueId：与 openOrder 同一笔订单；clientRequestId：幂等键，每次 checkout 必须新生成，禁止复用 openOrder 的 _open
          clientUniqueId: checkoutClientUniqueId,
          clientRequestId: checkoutClientRequestId,
          // Nuvei Simply Connect: 'prod' | 'int'（文档约定；省略则默认 prod）
          env: process.env.VUE_APP_BUILD_ENV.includes('prod') ? 'prod' : 'int',
          merchantSiteId,
          merchantId: merchantNo,
          country: data.FormData?.country || 'US',
          locale: locale.value, // "en",
          apmConfig: {
            applePay: {
              collectUserDetails: 'true', // false (default), true
              buttonStyle: 'black', // black (default), white, white-outline
              buttonType: 'plain', // buy (default), plain,
              // book, checkout, order, pay, subscribe, inStore,
              // addMoney, continue, contribute, donate, reload,
              // rent, setUp, support, tip, topUp
              buttonLocation: 'onTop', // onTop (default), gallery,
              '3Dflow': 'disable', // disable (default), enable
              companyName: 'Sun Pay',
            },
            GooglePay: {
              allowCreditCards: true,
              allowPrepaidCards: true,
              allowedCardAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
              assuranceDetailsRequired: true,
              billingAddressFormat: 'MIN',
              phoneNumberRequired: false,
              billingAddressRequired: true,
              checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
              gateway: 'nuveidigital',
              gatewayMerchantId: process.env.VUE_APP_BUILD_ENV.includes('prod')
                ? 'nuveidigital'
                : 'googletest', // 'googletest' for test or 'nuveidigital' for production
              merchantId: merchantNo, // or contact Nuvei Integration team to use your own ID
              merchantName: 'Sun Pay', // or your Google Pay name
              tokenizationType: 'PAYMENT_GATEWAY',
              totalPriceStatus: 'FINAL',
              '3Dflow': 'enable', // enable (default), disable
              buttonColor: 'white', // black (default), white
              buttonType: 'plain', // buy (default), book, checkout, order, pay, plain, subscribe
              buttonLocation: 'onTop', // onTop (default), gallery
              collectUserDetails: false,
            },
          },
          // pmWhitelist
          pmWhitelist,
          currency: data?.FormData?.currency,
          amount: data?.FormData?.amount,
          renderTo: '#flow-container',
          onResult: onNuveiCheckoutResult,
          autoOpenPM: false,
          alwaysCollectCvv: false,
          fullName: fullNameRaw,
          email,
          billingAddress: {
            ...(billingFirstName ? { firstName: billingFirstName } : {}),
            ...(billingLastName ? { lastName: billingLastName } : {}),
            email,
            country: billingCountry,
          },
        };
        /**
         * 打印完整 checkout 入参；订单关闭后仍可从 window 取回。
         *
         * 业务流程：
         * 1. 摘要关键字段
         * 2. JSON 可复制全文
         * 3. 挂到 window.__NUVEI_LAST_CHECKOUT__
         */
        try {
          const checkoutDump = {
            ...params,
            onResult: '[Function onNuveiCheckoutResult]',
          };
          // @ts-ignore
          window.__NUVEI_LAST_CHECKOUT__ = checkoutDump;
          // @ts-ignore
          window.__NUVEI_LAST_SESSION_TOKEN__ = paymentSessionObject;
          console.info('checkout request summary', {
            orderNo: data?.FormData?.orderNo,
            outOrderNo: data?.FormData?.outOrderNo,
            accountType: data?.FormData?.accountType,
            sessionToken: paymentSessionObject,
            clientUniqueId: params.clientUniqueId,
            clientRequestId: params.clientRequestId,
            merchantId: merchantNo,
            merchantSiteId,
            country: params.country,
            currency: params.currency,
            amount: params.amount,
            email,
            fullName: params.fullName,
            billingAddress: params.billingAddress,
            pmWhitelist,
          });
          console.info(`checkout request JSON:\n${JSON.stringify(checkoutDump, null, 2)}`);
          console.info('after close, run: copy(JSON.stringify(window.__NUVEI_LAST_CHECKOUT__, null, 2))');
        } catch (logErr) {
          console.warn('checkout dump failed', logErr);
          console.log(params);
        }
        checkout(params);
      });
    };

    // 监听推送
    // const ListenerOrderStatus = id => {
    //   const RealtimePush = Realtime.initRealtimePush(`${baseURL}ws/orders/${id}`, { id });
    //   if (RealtimePush) {
    //     RealtimePush.onmessage = event => {
    //       console.log("收到消息: " + event.data);
    //       if (event.data === "CANCEL") {
    //         showIframeUrl.value = false;
    //         data.FormData.orderStatus = 4;
    //       }
    //     };
    //   }
    // };

    const ftrToken = ref('');
    const embedBlocked = ref(false);
    onMounted(async () => {
      // HTML 抢跑已决定跳公共页：不再挂载业务，避免闪屏
      if ((window as any).__VGPAY_COMM_REDIRECTED__) {
        suppressPageReveal.value = true;
        isLoadingToast.value = true;
        isOpacity.value = true;
        return;
      }
      if (!payToken.value) {
        embedGuardReady.value = true;
        Toast({
          message: t('error'),
          overlay: false,
          duration: 5000,
        });
        return;
      }

      /**
       * 按 integration_type + 策略校验嵌入（非「凡嵌必拒」）：
       * hosted 等 RejectEmbeddedApi → 拒嵌；embedded_web → 须父域白名单。
       * 与订单开/关状态无关。策略失败且处于 iframe 时 fail-closed，避免误画状态页。
       * 顶层打开：Report 后台发送，不阻塞拉单（加速首屏）。
       * iframe 嵌入：仍 await Report，服务端硬拦时同步关页。
       * 同 token 60s 内刷新：策略/上报走 session 防抖，减少重复请求。
       */
      const isEmbedded = window.self !== window.top;

      /**
       * 拉取并应用嵌入策略。
       *
       * 业务流程：
       * 1. 优先读 session 缓存
       * 2. 否则请求 GetCashierEmbedPolicy
       * 3. 按决策设置 embedBlocked（iframe 失败 fail-closed）
       */
      const runEmbedPolicy = async () => {
        try {
          const cachedPolicy = tryGetCachedEmbedPolicy(payToken.value);
          if (cachedPolicy) {
            const decision = evaluateEmbedGuard(cachedPolicy);
            if (decision.block) {
              embedBlocked.value = true;
              console.warn('[CardPaySourceGuard] cashier embed blocked (cached)', decision.reason, cachedPolicy);
            }
            return;
          }
          const policyRes = await Cashier.GetCashierEmbedPolicy();
          if (policyRes?.code == 200) {
            setCachedEmbedPolicy(policyRes.data, payToken.value);
            // 只判断商户是否可嵌「收银台」；收银台内部 iframe 嵌渠道 3DS 不走此逻辑
            const decision = evaluateEmbedGuard(policyRes.data);
            if (decision.block) {
              embedBlocked.value = true;
              console.warn('[CardPaySourceGuard] cashier embed blocked', decision.reason, policyRes.data);
            }
          } else if (isEmbedded) {
            embedBlocked.value = true;
            console.warn('[CardPaySourceGuard] cashier embed policy non-200 while embedded', policyRes);
          }
        } catch (e) {
          if (isEmbedded) {
            embedBlocked.value = true;
            console.warn('[CardPaySourceGuard] GetCashierEmbedPolicy failed while embedded', e);
          } else {
            console.warn('[CardPaySourceGuard] GetCashierEmbedPolicy failed', e);
          }
        }
      };

      /**
       * 上报支付来源上下文。
       *
       * 业务流程：
       * 1. 组装 referrer / 是否嵌入 / UA / parent_origin
       * 2. iframe：await 上报，硬拦时设 embedBlocked
       * 3. 顶层：后台发送，失败仅打日志，不挡首屏拉单
       */
      const reportPaySource = async () => {
        try {
          const reportPayload = {
            referrer: document.referrer || "",
            is_embedded: isEmbedded,
            page_url: window.location.href,
            user_agent: navigator.userAgent || "",
            parent_origin: isEmbedded ? (resolveParentOrigin() || "") : ""
          };
          if (shouldSkipSourceReport(reportPayload, payToken.value)) {
            console.info('[CardPaySourceGuard] ReportPaySourceContext skipped (debounce)');
            return;
          }
          const reportRes = await Cashier.ReportPaySourceContext(reportPayload);
          if (isEmbedded && reportRes && reportRes.code != null && reportRes.code != 200) {
            embedBlocked.value = true;
            console.warn('[CardPaySourceGuard] ReportPaySourceContext rejected while embedded', reportRes);
          } else if (reportRes && reportRes.code == 200) {
            markSourceReportDone(reportPayload, payToken.value);
          }
        } catch (e) {
          console.warn('[CardPaySourceGuard] ReportPaySourceContext failed', e);
          if (isEmbedded) {
            embedBlocked.value = true;
          }
        }
      };

      if (isEmbedded) {
        // iframe：必须先策略+上报，避免硬拦前先画出支付页
        await runEmbedPolicy();
        await reportPaySource();
        if (embedBlocked.value) {
          embedGuardReady.value = true;
          return;
        }
        if (isSunpayBuild) {
          // 先挂载 SunPay 收银台 DOM，再由 loading/opacity 遮罩等待接口；
          // 避免接口链路异常时三个模板门闩同时为 false 导致整页空白。
          embedGuardReady.value = true;
          await getChannelList();
        } else {
          embedGuardReady.value = true;
          getChannelList();
        }
      } else {
        if (isSunpayBuild) {
          // 顶层访问同样先挂壳，确保动态表单及第三方组件有真实挂载节点。
          embedGuardReady.value = true;
          await getChannelList();
        } else {
          embedGuardReady.value = true;
          getChannelList();
        }
        // 顶层直开与 iframe 嵌入无关，跳过策略/上报，避免首屏多 2 次查单打满 DB
      }

      // 监听风控事件
      document.addEventListener('ftr:tokenReady', (evt) => {
        // @ts-ignore
        ftrToken.value = evt?.detail; // Retrieve the token to be sent to your back-end
      });

      GetRemeberme();

      trackEvent('page_view', {
        page_title: 'Checkout',
        page_path: window.location.href,
      });

      window.addEventListener('message', onPayfacTransitionMessage);
      // 预热 WeTech（成功缓存 device_id；失败仅日志）
      const warmupPayload = await getWetechDevicePayload();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('message', onPayfacTransitionMessage);
    });

    /// ============== Card Form ==============
    const cardFormData = ref({
      cardHolderName: '',
      cardNumber: '',
      expriyDate: '',
      cvv: '',
    });
    // 校验函数可以直接返回一段错误提示
    const validatorCardNumber = (val) => {
      if (!val) {
        return t('required');
      } if (val.startsWith('1')) {
        return t('cashier.jcb');
      } if (val.startsWith('3')) {
        return t('cashier.amex');
      } if (val.startsWith('8')) {
        return t('cashier.china');
      } if (val.startsWith('9')) {
        return t('cashier.mada');
      } if (val.length >= 6) {
        // 取前6位（自动过滤空格、短横线等）
        const bin6 = String(val)
          .replace(/\D/g, '')
          .slice(0, 6);

        const num = parseInt(bin6, 10);
        if (num >= 622126 && num <= 622925) {
          return t('cashier.china');
        }
      } else if (val.length > 23) {
        return t('cashier.incomplete');
      } else {
        return true;
      }
    };
    const validatorDate = (val) => {
      // 支持 / 和 - 分隔符，统一处理
      if (!val) return t('required');

      // 关键：只要不是完整合法的 MM/YY 或 MM-YY 格式，一律通过！
      // 这样 formatter 随便怎么跳，都不会因为 validator 返回错误而引发循环
      const trimmed = val.trim();
      if (trimmed.length < 5) return true;

      const parts = val.trim().split(/[/\-]/);
      if (parts.length !== 2) return t('cashier.expiry');

      const inputMonth = parseInt(parts[0], 10); // MM
      const inputYear = parseInt(parts[1], 10); // YY

      // 基础校验
      if (
        isNaN(inputMonth)
        || isNaN(inputYear)
        || inputMonth < 1
        || inputMonth > 12
        || inputYear < 0
        || inputYear > 99
      ) {
        return t('cashier.expiry');
      }

      const now = new Date();
      const currentYear = now.getFullYear() % 100; // 当前年的后两位，例如 2025 → 25
      const currentMonth = now.getMonth() + 1; // getMonth() 是 0~11，所以 +1

      // 转成完整年份进行比较（YY → 20YY）
      const fullInputYear = 2000 + inputYear;

      if (fullInputYear < now.getFullYear()) {
        return t('cashier.expiry');
      }

      // 1. 年份比当前年大 → 肯定有效
      if (fullInputYear > now.getFullYear()) {
        return true;
      }

      // 2. 年份相等 → 再比较月份
      if (fullInputYear == now.getFullYear()) {
        return inputMonth >= currentMonth ? true : t('cashier.expiry');
      }
    };

    const formatterNumber = (val) => {
      if (!val) return '';

      // 1. 先清除所有非数字字符
      const digits = val.toString().replace(/\D/g, '');

      // 2. 每4位插入一个空格
      // 方法一：正则分组（最简洁高效）
      return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    };

    const formatterDate = (e) => {
      // 正确获取输入框当前值（兼容所有情况）
      const val = typeof e === 'string' ? e : e.target?.value || '';

      // 只保留数字，最多4位
      const digits = val.replace(/\D/g, '').slice(0, 4);
      let formatted = '';

      // 关键：删除阶段直接返回纯数字（解决删不掉 /）
      if (val.endsWith('/') && digits.length <= 2) {
        formatted = digits;
      }
      // 正常输入流程
      else if (digits.length === 1) {
        formatted = digits >= '2' && digits <= '9' ? `0${digits}/` : digits;
      } else if (digits.length === 2) {
        const m = parseInt(digits, 10);
        formatted = m > 12 ? `0${digits[0]}/${digits[1]}` : `${digits}`;
      } else {
        // 3位或4位
        const month = digits.slice(0, 2);
        const year = digits.slice(2);
        const m = parseInt(month, 10);
        if (m > 12) {
          formatted = `0${digits[0]}/${digits.slice(1)}`;
        } else {
          formatted = `${month}/${year}`;
        }
      }

      // 关键防死循环：只有真正变化时才更新 v-model
      if (cardFormData.value.expriyDate !== formatted && formatted !== '/') {
        cardFormData.value.expriyDate = formatted;
      }
    };

    const getAcceptHeader = () => {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('chrome') || ua.includes('edg')) {
        return 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7';
      } if (ua.includes('firefox')) {
        return 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8';
      } if (ua.includes('safari')) {
        return 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8';
      }
      return 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8';
    };

    // ============== Remeber me ==============
    const SetRemeberme = (data) => {
      if (remeberCheck.value) {
        localStorage.setItem(key, JSON.stringify(data));
      } else {
        localStorage.removeItem(key);
      }
    };
    const GetRemeberme = () => {
      const cardInfo = localStorage.getItem(key) || null;
      if (cardInfo) {
        // 赋值 & 勾选
        const info = JSON.parse(cardInfo);
        cardFormData.value.cardHolderName = info.cardHolderName;
        cardFormData.value.cardNumber = info.cardNumber;
        remeberCheck.value = true;
      }
    };
    const parseCardExpiry = () => {
      const raw = (cardFormData.value.expriyDate || '').trim();
      const parts = raw.split('/').map((part) => part.trim());
      if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return null;
      }
      const month = parts[0].padStart(2, '0');
      let year = parts[1];
      if (year.length === 2) {
        year = `20${year}`;
      }
      return {
        month: parseInt(month, 10),
        year: parseInt(year, 10),
      };
    };

    // ============== Card Pay ==============
    const CreateCardPay = async () => {
      trackEvent('card_button_pay', {
        merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
        order_no: data?.FormData?.orderNo,
        button_type: 'CARD',
        event: 'click',
      });
      const expiry = parseCardExpiry();
      if (!expiry || !cardFormData.value.cardNumber || !cardFormData.value.cvv) {
        Toast({
          message: t('required'),
          overlay: false,
          duration: 3000,
        });
        return;
      }
      // UnLimitPayfac：未 CARDFORM 预下单时先 payin，再 pay
      const needsPayfacPreOrder = isUnlimitPayfacChannel(data.FormData.channelCode)
        && data.FormData.actionType !== 'CARDFORM';
      if (needsPayfacPreOrder) {
        await CreateTopUpOrder();
        if (data.FormData.actionType !== 'CARDFORM' || Number(data.FormData.orderStatus) !== 1) {
          isLoadingToast.value = false;
          return;
        }
      }
      SetRemeberme({
        cardHolderName: cardFormData.value.cardHolderName,
        cardNumber: cardFormData.value.cardNumber,
      });
      // const loadingToast = Toast({
      //   message: "",
      //   type: "loading",
      //   overlay: true,
      //   duration: 0
      // });
      isLoadingToast.value = true;
      const wetechPayload = await getWetechDevicePayload();
      const params = {
        card_holder_name: cardFormData.value.cardHolderName,
        card_number: cardFormData.value.cardNumber.replaceAll(/\s/g, ''),
        card_cvv: cardFormData.value.cvv,
        card_expiry_month: expiry.month,
        card_expiry_year: expiry.year,
        // Forter Token 有值时才传，避免空 risk_token 影响后端风控判断
        ...(ftrToken.value ? { risk_token: ftrToken.value } : {}),
        // 成功带 device_id；失败不传字段、不传错误信息
        ...(wetechPayload.device_id ? { device_id: wetechPayload.device_id } : {}),
        user_agent: navigator.userAgent,

        accept_header: getAcceptHeader(),
        color_depth: screen.colorDepth,
        java_enabled: !!(navigator.javaEnabled && navigator.javaEnabled()),
        java_script_enabled: true,
        language: navigator.language || 'unknown',
        screen_height: screen.height,
        screen_width: screen.width,
        time_zone: new Date().getTimezoneOffset(),
      };
      trackEvent('purchase', {
        merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
        order_no: data?.FormData?.orderNo,
        value: data?.FormData?.amount,
        currency: data?.FormData?.currency,
      });
      const res = process.env.VUE_APP_TYPE == 'web2'
        ? await Cashier.CreateCardPayWeb2(params)
        : await Cashier.CreateCardPay(params);
      // loadingToast.close();
      isLoadingToast.value = false;
      if (res?.code == 200) {
        trackEvent('payment_method', {
          button_type: 'CARD',
          event: 'click',
          result: 'success',
          merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
          order_no: data?.FormData?.orderNo,
        });
        if (res?.data) {
          if (res?.data?.risk_status && res?.data?.risk_status !== 'SUCCESS') {
            Toast({
              message: res?.data?.risk_msg,
              overlay: false,
              duration: 5000,
            });
            return;
          }

          if (res?.data?.action_href) {
            const channelCode = data.FormData.channelCode || res?.data?.channelCode;
            const accountType = data.FormData.accountType || res?.data?.accountType;
            // @ts-ignore
            data.FormData.actionType = 'REDIRECT';
            data.FormData.checkoutUrl = res?.data?.action_href;
            if (
              openPayablTopRedirectIfNeeded(
                res?.data?.action_href,
                channelCode,
                accountType,
              )
            ) {
              return;
            }
            if (res?.data?.challenge_required) {
              data.FormData.challengeRequired = res?.data?.challenge_required;
              setTimeout(() => {
                showIframeUrl.value = true;
                NuveiForm.value.submit();
                setTimeout(() => {
                  getOrderInfo();
                }, 200);
                // loadingToast.close();
                isLoadingToast.value = false;
              }, 200);
            } else {
              showIframeUrl.value = true;
              iframeUrl.value = res?.data?.action_href;
              data.FormData.checkoutUrl = res?.data?.action_href;
              // @ts-ignore
              data.FormData.actionType = 'REDIRECT';
              RedirectURL();
              // UnLimitPayfac：3DS iframe 跨域时 RedirectURL 可能无法触发顶层跳转，主动轮询订单状态
              if (isUnlimitPayfacChannel(data.FormData.channelCode)) {
                setTimeout(() => {
                  getStatus();
                }, 3000);
              }
            }
          }
        }
      } else {
        trackEvent('payment_method', {
          merchant_no: data?.FormData?.merchantNo || data?.FormData?.businessId,
          order_no: data?.FormData?.orderNo,
          button_type: 'CARD',
          event: 'click',
          result: 'fail',
        });
      }
    };

    return {
      lang,
      BASE_URL,
      locale,
      resverText,
      from,
      isCanSerialize,
      isOpacity,
      silentAutoPreOrder,
      autoPreOrderHidePaytype,
      showSunpayPay,
      remeberCheck,
      isLoadingToast,
      embedBlocked,
      embedGuardReady,
      isSunpayBootLoading,

      isUnlimitPayfacChannel,
      isSunpayCashier,
      showPayfacCardFormEarly,
      showPayfacCardFormPanel,
      showPayfacTransition,
      showPayfacIframeLoadingMask,
      payfacTransitionTitle,
      payfacTransitionMessage,
      data,
      currentLang,
      showPopover,
      showSupplierSheet,
      showBankSheet,
      showCalendar,
      onConfirmDate,
      changeLang,
      getLangName,
      FormatDateZone,
      copy,
      loaded,
      isFinish,
      finish,

      splitNumber,
      paymentUrl,
      isDisableConfirm,
      step,
      steps,
      nextStep,

      showIframeUrl,
      iframeUrl,
      toPay,
      result,

      CashPayInOrderStatus,
      LinkedPayForm,
      CashChannelList,
      PaymentTypeList,
      getNameByCode,
      onDateTimePickerHandler,

      SupplierList,
      onSelectChange,
      BankList,
      onSelectBank,

      dynamicsFiledList,
      showDwonMore,

      cardFormData,
      NuveiForm,
      validatorCardNumber,
      validatorDate,
      formatterNumber,
      formatterDate,
      CreateCardPay,
    };
  },
});
